import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CREAM, RED, BLACK } from '../components/Layout';
import { useCart } from '../hooks/useCart';
import { CLIENT_ID } from '../firebaseConfig';

const SMARTSHOP_API = 'https://europe-west1-dbos-core-prod.cloudfunctions.net';
const AGENT_URL = 'https://essaness-search-agent-273538020820.europe-west1.run.app/checkout';

const REGION_SHIPPING: Record<string, { label: string; flag: string; rate: string; price: number; note?: string }> = {
  IE:    { label: 'Ireland', flag: 'https://flagcdn.com/w20/ie.png', rate: 'shr_1TMPfxGLwGIe4hAjkiCXdEQZ', price: 5.99 },
  NI:    { label: 'Northern Ireland', flag: 'https://flagcdn.com/w20/gb.png', rate: 'shr_1TMPjmGLwGIe4hAjmIeyUmPs', price: 8.99 },
  EU:    { label: 'Europe', flag: 'https://flagcdn.com/w20/eu.png', rate: 'shr_1TMRsZGLwGIe4hAj1j5L9TkF', price: 29.99 },
  UK:    { label: 'UK', flag: 'https://flagcdn.com/w20/gb.png', rate: 'shr_1TMRl4GLwGIe4hAjWTELSv6v', price: 29.99 },
  LARGE: { label: 'Large Box — Ireland', flag: 'https://flagcdn.com/w20/ie.png', rate: 'shr_1TMRx4GLwGIe4hAj2i9OcZfQ', price: 15.00 },
};

const CheckoutModal: React.FC<{
  product: Product;
  onClose: () => void;
}> = ({ product, onClose }) => {
  const [pickup, setPickup] = useState(false);
  const [region, setRegion] = useState<string>('IE');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [buying, setBuying] = useState(false);
  const { addToCart, adding } = useCart();
  const [cartMsg, setCartMsg] = useState<string | null>(null);

  const isLargeBox = product.large_box || false;
  const shipping = pickup ? null : (
    isLargeBox && (region === 'IE' || region === 'NI')
      ? { ...REGION_SHIPPING['LARGE'], label: region === 'NI' ? 'Large Box — Northern Ireland' : 'Large Box — Ireland' }
      : REGION_SHIPPING[region] || REGION_SHIPPING['IE']
  );
  const shippingRate = pickup ? 'shr_1TMPhvGLwGIe4hAjN32hEw8k' : (shipping?.rate || REGION_SHIPPING['IE'].rate);
  const shippingCost = shipping?.price || 0;
  const total = (product.glen_price + shippingCost).toFixed(2);
  const canProceed = termsAccepted;

  const handleProceed = async () => {
    if (!canProceed) return;
    setBuying(true);
    try {
      const res = await fetch(AGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gid: product.gid,
          large_box: product.large_box || false,
          shipping_rate: shippingRate,
          success_url: `${window.location.origin}/success`,
          cancel_url: window.location.href,
        })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error);
    } catch (e) {
      alert('Payment error. Please call us on 056 776 5693.');
    } finally {
      setBuying(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#0a0c0a', border: '1px solid rgba(255,255,255,0.1)', padding: 32, maxWidth: 420, width: '100%', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer' }}>✕</button>

        <h3 style={{ color: '#F4F1E1', fontWeight: 900, fontStyle: 'italic', fontSize: 20, margin: '0 0 4px', fontFamily: 'Georgia, serif' }}>{product.name}</h3>
        <p style={{ color: '#8B1A1A', fontWeight: 900, fontSize: 22, margin: '0 0 18px' }}>€{product.glen_price?.toFixed(2)}</p>

        {/* Pickup toggle */}
        <div onClick={() => setPickup(!pickup)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: pickup ? 'rgba(139,26,26,0.15)' : '#141614', border: `1px solid ${pickup ? 'rgba(139,26,26,0.5)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer', marginBottom: 10, transition: 'all 0.2s' }}>
          <div>
            <p style={{ color: '#F4F1E1', fontWeight: 700, fontSize: 13, margin: '0 0 2px' }}>🏪 Free In-Store Pickup</p>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>No1 St Kieran St, Kilkenny · Free</p>
          </div>
          <div style={{ width: 36, height: 20, background: pickup ? '#8B1A1A' : 'rgba(255,255,255,0.1)', borderRadius: 10, position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
            <div style={{ position: 'absolute', width: 14, height: 14, background: 'white', borderRadius: '50%', top: 3, left: pickup ? 19 : 3, transition: 'left 0.2s' }} />
          </div>
        </div>

        {/* Delivery dropdown — no prices shown here */}
        {!pickup && (
          <div style={{ background: '#141614', border: '1px solid rgba(255,255,255,0.08)', padding: '14px 16px', marginBottom: 10 }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>📦 Ship to my address</p>
            <select value={region} onChange={e => setRegion(e.target.value)}
              style={{ width: '100%', background: '#0a0c0a', border: '1px solid rgba(255,255,255,0.15)', color: '#F4F1E1', padding: '11px 14px', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              <option value="IE">🇮🇪 Ireland</option>
              <option value="NI">🇬🇧 Northern Ireland</option>
              <option value="EU">🇪🇺 Europe</option>
              <option value="UK">🇬🇧 United Kingdom</option>
            </select>
            {region === 'UK' && <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', margin: '8px 0 0', fontStyle: 'italic' }}>UK: customer is responsible for any import duties upon delivery.</p>}
            {isLargeBox && <p style={{ fontSize: 10, color: 'rgba(255,200,100,0.6)', margin: '8px 0 0' }}>⚠️ Large/heavy item — oversized shipping rate applies.</p>}
          </div>
        )}

        {/* Order summary */}
        <div style={{ background: '#0a0c0a', border: '1px solid rgba(255,255,255,0.06)', padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>Product</span>
            <span style={{ color: 'rgba(255,255,255,0.7)' }}>€{product.glen_price?.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>{pickup ? 'Pickup' : `Shipping${isLargeBox ? ' (large box)' : ''}`}</span>
            <span style={{ color: pickup ? '#4CAF50' : '#8B1A1A', fontWeight: 700 }}>{pickup ? 'Free' : `€${shippingCost.toFixed(2)}`}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 2px', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 6 }}>
            <span style={{ color: '#F4F1E1', fontWeight: 900, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</span>
            <span style={{ color: '#F4F1E1', fontWeight: 900, fontSize: 20 }}>€{total}</span>
          </div>
        </div>

        {/* Terms */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
          <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
            I agree to the <a href="/terms" target="_blank" style={{color:'#8B1A1A'}}>Terms & Conditions</a> and <a href="/privacy" target="_blank" style={{color:'#8B1A1A'}}>Privacy Policy</a>.
          </span>
        </label>

        <button onClick={handleProceed} disabled={!canProceed || buying}
          style={{ width: '100%', background: canProceed ? '#8B1A1A' : 'rgba(255,255,255,0.15)', color: 'white', border: 'none', padding: '16px', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', cursor: canProceed ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}>
          {buying ? 'Redirecting to Stripe...' : canProceed ? (pickup ? `Confirm Pickup — €${product.glen_price?.toFixed(2)}` : `Proceed to Payment — €${total}`) : 'Please accept terms to continue'}
        </button>

        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center', margin: '12px 0 0' }}>
          Secure payment via Stripe · VAT included
        </p>
      </div>
    </div>
  );
};

// D-048: a variant is a purchasable entity with its own stock, its own optional price
// (price_cents null = inherit the product price) and its own optional image. The cart
// and the order record variant_id, never the slug.
interface Variant {
  variant_id: string;
  name: string;
  price_cents: number | null;
  stock_status: string;
  image: string | null;
  published?: boolean;
}

interface Product {
  gid: string;
  slug?: string;
  name: string;
  category: string;
  glen_price: number;
  primary_image: string;
  secondary_image: string;
  all_assets: string[];
  stock_status: string;
  description: string;
  technical_specs: string;
  hygiene_sensitive: boolean;
  large_box: boolean;
  published: boolean;
  variants: Variant[];
}

export const ProductPage: React.FC<{ lang: string }> = ({ lang }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [specsOpen, setSpecsOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const { addToCart, adding } = useCart();
  const [cartMsg, setCartMsg] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [variant, setVariant] = useState<Variant | null>(null);
  const cycleRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-cycle images — 3.5s zoom then flip, loops continuously
  useEffect(() => {
    if (!product) return;
    // MUST stay identical to the `images` array built below for the thumbnails -
    // same members, same order. These were built differently (this one omitted
    // secondary_image), so a product with three images had a 2-item cycle against a
    // 3-item gallery: the third thumbnail set an index the cycle could never reach
    // and the flip died. Change one, change both.
    const imgs = [variant?.image, product.primary_image, product.secondary_image, ...(product.all_assets || [])]
      .filter((img, i, arr) => img && arr.indexOf(img) === i);
    // A chosen variant with its own photo holds on it. Without one there is nothing
    // specific to show, so the product's own slideshow carries on.
    if (variant?.image) { setActiveImage(0); setZoomed(true); return; }
    if (imgs.length < 2) { setZoomed(true); return; }
    setZoomed(true);
    cycleRef.current = setInterval(() => {
      setZoomed(false);
      setTimeout(() => {
        setActiveImage(prev => {
          const next = (prev + 1) % imgs.length;
          return next;
        });
        setZoomed(true);
      }, 400);
    }, 3500);
    return () => { if (cycleRef.current) clearInterval(cycleRef.current); };
    // `variant` is in here deliberately: choosing a colour rebuilds the gallery with
    // that colour first, and the cycle must stop on it rather than scroll away from
    // the answer the shopper just asked for.
  }, [product, variant]);

  // Stop auto-cycle when user manually selects image
  const handleManualSelect = (i: number) => {
    if (cycleRef.current) clearInterval(cycleRef.current);
    setActiveImage(i);
    setZoomed(true);
  };

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        // gido_id was missing: smartshop-get-product falls back to an env-var default
        // tenant when it is absent (D-036), so this page only ever worked because the
        // default happens to be this shop. Sent explicitly now.
        const res = await fetch(`${SMARTSHOP_API}/smartshop-get-product`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, gido_id: CLIENT_ID }),
        });
        const data = await res.json();
        const p = data.product;
        if (p) {
          setProduct({
            gid: p.gid, slug: p.slug, name: p.name, category: p.category,
            glen_price: p.price, primary_image: p.primary_image, secondary_image: p.secondary_image,
            all_assets: p.all_assets, stock_status: p.stock_status, description: p.description,
            technical_specs: p.technical_specs || '', hygiene_sensitive: !!p.hygiene_sensitive,
            large_box: !!p.large_box, published: p.status === 'active',
            variants: Array.isArray(p.variants) ? p.variants.filter((v: Variant) => v.published !== false) : [],
          } as Product);
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  }, [slug]);

  const handleBuy = () => {
    if (!product) return;
    setShowModal(true);
  };

  if (loading) return <div style={{ padding: '80px 40px', textAlign: 'center', color: 'rgba(0,0,0,0.4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Loading product...</div>;
  if (!product) return <div style={{ padding: '80px 40px', textAlign: 'center' }}>
    <p style={{ color: 'rgba(0,0,0,0.4)', marginBottom: 20 }}>Product not found.</p>
    <button onClick={() => navigate('/shop')} style={{ background: RED, color: 'white', border: 'none', padding: '12px 24px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>Back to Shop</button>
  </div>;

  // D-048 resolution. A variant's price is ABSOLUTE and null means inherit the product
  // price, so ?? and never || - a variant priced at zero must stay zero. Its stock and
  // its image override the product's the same way.
  const effPrice = (variant?.price_cents != null ? variant.price_cents / 100 : null) ?? product.glen_price;
  const effStock = variant?.stock_status ?? product.stock_status;
  const needsVariant = (product.variants?.length ?? 0) > 0 && !variant;

  // The chosen variant's own picture comes first; the product's follow, deduplicated.
  const images = [variant?.image, product.primary_image, product.secondary_image, ...(product.all_assets || [])]
    .filter((img, idx, arr) => img && arr.indexOf(img) === idx) as string[];

  return (
    <div id='product-wrap' style={{ padding: '40px 40px 80px', maxWidth: 1100, margin: '0 auto', background: 'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(139,26,26,0.05) 0%, rgba(244,241,225,0) 60%)' }}>
      <style>{`
        @media(max-width:860px){
          #product-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          #product-wrap { padding: 20px 16px 60px !important; }
        }
      `}</style>
      {showModal && product && <CheckoutModal product={product} onClose={() => setShowModal(false)} />}
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(0,0,0,0.4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', marginBottom: 32, padding: 0 }}>
        ← Back
      </button>

      <div id='product-grid' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56 }}>

        <div>
          <div style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 12, aspectRatio: '1', position: 'relative' }}>
            {images.map((img, i) => (
              <img key={i} src={img} alt={product.name} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
                opacity: activeImage === i ? 1 : 0,
                transform: activeImage === i && zoomed ? 'scale(1.06)' : 'scale(1)',
                transition: 'opacity 0.4s ease-in-out, transform 3.5s ease-in-out',
                zIndex: activeImage === i ? 1 : 0,
              }} />
            ))}
          </div>
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {images.map((img, i) => (
                <div key={i} onClick={() => handleManualSelect(i)}
                  style={{ width: 64, height: 64, border: `2px solid ${i === activeImage ? RED : 'rgba(0,0,0,0.1)'}`, overflow: 'hidden', cursor: 'pointer', flexShrink: 0 }}>
                  <img src={img} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(0,0,0,0.4)', margin: '0 0 8px', cursor: 'pointer' }}
              onClick={() => navigate(`/shop/${product.category.toLowerCase().replace(/\s+/g, '-')}`)}>
              {product.category}
            </p>
            <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,3vw,2.4rem)', color: BLACK, margin: '0 0 16px', lineHeight: 1.1 }}>
              {product.name}
            </h1>
            <p style={{ fontWeight: 900, fontSize: 28, color: RED, margin: 0 }}>
              €{effPrice?.toFixed(2)}
            </p>
            <p style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 0' }}>
              Incl. VAT
            </p>
          </div>

          <div style={{ width: 40, height: 3, background: RED }} />

          {/* Variant picker (D-048). Sold-out variants are shown struck through rather
              than hidden, so a shopper can see the colour exists and ask for it. */}
          {product.variants?.length > 0 && (
            <div>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(0,0,0,0.45)', margin: '0 0 8px' }}>
                Choose one
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {product.variants.map((v) => {
                  const vSoldOut = v.stock_status === 'out_of_stock';
                  const sel = variant?.variant_id === v.variant_id;
                  return (
                    <button key={v.variant_id} type="button" disabled={vSoldOut}
                      onClick={() => { setVariant(v); setActiveImage(0); }}
                      style={{
                        padding: '9px 15px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                        cursor: vSoldOut ? 'not-allowed' : 'pointer',
                        border: sel ? `2px solid ${RED}` : '1px solid rgba(0,0,0,0.25)',
                        background: sel ? 'rgba(139,26,26,0.09)' : 'white', color: BLACK,
                        opacity: vSoldOut ? 0.4 : 1,
                        textDecoration: vSoldOut ? 'line-through' : 'none',
                        transition: 'all 0.15s',
                      }}>
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.description && (
            <p style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(0,0,0,0.65)', margin: 0 }}>
              {product.description}
            </p>
          )}

          {product.technical_specs && (
            <div style={{ border: '1px solid rgba(0,0,0,0.1)' }}>
              <button onClick={() => setSpecsOpen(!specsOpen)}
                style={{ width: '100%', textAlign: 'left', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: BLACK }}>
                Technical Specifications
                <span style={{ fontSize: 16, opacity: 0.4 }}>{specsOpen ? '▴' : '▾'}</span>
              </button>
              {specsOpen && (
                <div style={{ padding: '0 16px 16px', fontSize: 12, lineHeight: 1.8, color: 'rgba(0,0,0,0.6)', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 12, whiteSpace: 'pre-wrap' }}>
                  {product.technical_specs}
                </div>
              )}
            </div>
          )}

          {product.hygiene_sensitive && (
            <div style={{ background: 'rgba(139,26,26,0.06)', border: '1px solid rgba(139,26,26,0.2)', padding: '10px 14px' }}>
              <p style={{ fontSize: 11, color: RED, fontWeight: 700, margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Hygiene Notice</p>
              <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.6)', margin: 0 }}>This item cannot be returned if the seal is broken — EU consumer policy.</p>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: effStock === 'out_of_stock' ? '#999' : effStock === 'low_stock' ? '#d08700' : '#2d9e2d', display: 'inline-block' }} />
            <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>
              {effStock === 'in_stock' ? 'In Stock' : effStock === 'low_stock' ? 'Only a few left' : effStock === 'pre_order' ? 'Pre-Order' : 'Out of Stock'}
            </span>
          </div>

          <button onClick={handleBuy} disabled={buying || effStock === 'out_of_stock' || needsVariant}
            style={{ background: product.stock_status === 'out_of_stock' ? 'rgba(0,0,0,0.2)' : RED, color: 'white', border: 'none', padding: '16px 32px', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', cursor: product.stock_status === 'out_of_stock' ? 'not-allowed' : 'pointer', transition: 'background 0.2s', width: '100%' }}>
            {buying ? 'Redirecting to payment...' : effStock === 'out_of_stock' ? 'Out of Stock' : needsVariant ? 'Choose before buying' : `Buy Now — €${effPrice?.toFixed(2)}`}
          </button>

          <button onClick={async () => { try { await addToCart(product.gid, { variantId: variant?.variant_id }); setCartMsg("Added to cart!"); } catch { setCartMsg("Could not add to cart."); } finally { setTimeout(() => setCartMsg(null), 2000); } }}
            disabled={adding || effStock === 'out_of_stock' || needsVariant}
            style={{ background: 'none', border: `1px solid ${RED}`, color: RED, padding: '14px 32px', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', cursor: 'pointer', width: '100%', marginTop: 8 }}>
            {adding ? 'Adding...' : needsVariant ? 'Choose before adding' : 'Add to Cart'}
          </button>
          {cartMsg && <p style={{ fontSize: 11, color: '#4CAF50', textAlign: 'center', margin: '6px 0 0' }}>{cartMsg}</p>}

          <p style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', textAlign: 'center', margin: 0, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Secure payment via Stripe · VAT included
          </p>
        </div>
      </div>
    </div>
  );
};
